import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useFetch from "hooks/useFetch";
import { ApiKey, formatDateTime } from "pages/ApiPage/ApiPage";
import {
  Alert,
  Badge,
  Button,
  Card,
  Column,
  ConfirmDialog,
  EmptyState,
  Icon,
  PageHeader,
  Select,
  Skeleton,
  Table,
  TextField,
  Toggle,
} from "stories/Admin";
import PageModal from "../LocationsPage/PageModal";
import styles from "./ApiKeysPage.module.scss";

/*
 * Superadmin: alle API-nøkler moskeene har laget, hvem som laget dem, når og hvor de er brukt
 * (domene, siste IP og nettleser), med mulighet til å trekke dem tilbake. Superadmin kan også
 * lage en nøkkel for hvilken som helst moské; nøkkelen vises bare én gang.
 */

interface MosqueOption {
  orgNr: string;
  orgName: string;
  name?: string;
}

const ApiKeysPage: React.FC = () => {
  const { execute } = useFetch<any>();
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showRevoked, setShowRevoked] = useState(false);
  const [details, setDetails] = useState<ApiKey | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const [mosques, setMosques] = useState<MosqueOption[] | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createOrgNr, setCreateOrgNr] = useState("");
  const [createName, setCreateName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<{
    key: string;
    mosque: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    const result = await executeRef.current("/api/v1/admin/api-keys", "GET");
    setKeys(Array.isArray(result) ? result : []);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return keys.filter(
      (k) =>
        (showRevoked || !k.revokedAt) &&
        (!q ||
          (k.mosqueName || "").toLowerCase().includes(q) ||
          k.orgNr.includes(q) ||
          k.name.toLowerCase().includes(q) ||
          (k.createdByEmail || "").toLowerCase().includes(q) ||
          k.origins.some((o) => o.origin.toLowerCase().includes(q))),
    );
  }, [keys, search, showRevoked]);

  const active = keys.filter((k) => !k.revokedAt);
  const usedLast30 = active.filter((k) => k.origins.length > 0).length;
  const calls30 = keys.reduce(
    (s, k) => s + k.origins.reduce((a, o) => a + o.requests, 0),
    0,
  );

  const openCreate = async () => {
    setCreateOpen(true);
    setCreateOrgNr("");
    setCreateName("");
    setCreateError(null);
    if (mosques === null) {
      const result = await executeRef.current("/api/v1/mosques", "GET");
      setMosques(
        (Array.isArray(result) ? (result as MosqueOption[]) : [])
          .filter((m) => m.orgNr)
          .sort((a, b) =>
            (a.name || a.orgName).localeCompare(b.name || b.orgName, "nb"),
          ),
      );
    }
  };

  const mosqueLabel = (orgNr: string) => {
    const m = mosques?.find((x) => x.orgNr === orgNr);
    return m ? m.name || m.orgName : orgNr;
  };

  const create = async () => {
    if (!createOrgNr) return;
    setCreating(true);
    setCreateError(null);
    const result = await executeRef.current(
      `/api/v1/mosques/${encodeURIComponent(createOrgNr)}/api-keys`,
      "POST",
      { name: createName.trim() || "Nettsiden" },
    );
    setCreating(false);
    if (result?.key) {
      setCreateOpen(false);
      setNewKey({ key: result.key, mosque: mosqueLabel(createOrgNr) });
      setCopied(false);
      load();
    } else {
      setCreateError("Kunne ikke lage nøkkelen. Prøv igjen.");
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const revoke = async () => {
    if (!revoking) return;
    await executeRef.current(`/api/v1/admin/api-keys/${revoking.id}`, "DELETE");
    setRevoking(null);
    setDetails(null);
    load();
  };

  type Origin = ApiKey["origins"][number];

  const columns: Array<Column<ApiKey>> = [
    {
      key: "mosque",
      header: "Moské",
      render: (k) => (
        <span className={styles.stack}>
          <b>{k.mosqueName || k.orgNr}</b>
          <span className={styles.muted}>{k.orgNr}</span>
        </span>
      ),
    },
    {
      key: "key",
      header: "Nøkkel",
      render: (k) => (
        <span className={styles.stack}>
          <span>{k.name}</span>
          <span className={styles.inline}>
            <code className={styles.code}>{k.keyPrefix}…</code>
            {k.revokedAt && <Badge tone="grey">Trukket tilbake</Badge>}
          </span>
        </span>
      ),
    },
    {
      key: "by",
      header: "Laget av",
      render: (k) => (
        <span className={styles.muted}>{k.createdByEmail || "–"}</span>
      ),
    },
    {
      key: "created",
      header: "Laget",
      numeric: true,
      render: (k) => (
        <span className={styles.muted}>{formatDateTime(k.createdAt)}</span>
      ),
    },
    {
      key: "last",
      header: "Sist brukt",
      numeric: true,
      render: (k) => (
        <span className={styles.muted}>{formatDateTime(k.lastUsedAt)}</span>
      ),
    },
    {
      key: "count",
      header: "Kall totalt",
      numeric: true,
      align: "right",
      render: (k) => k.usageCount.toLocaleString("nb-NO"),
    },
    {
      key: "origins",
      header: "Brukt fra",
      render: (k) => (
        <span className={styles.stack}>
          {k.origins.length === 0 ? (
            <span className={styles.muted}>–</span>
          ) : (
            k.origins
              .slice(0, 2)
              .map((o) => <span key={o.origin}>{o.origin}</span>)
          )}
          {k.origins.length > 2 && (
            <span className={styles.muted}>+{k.origins.length - 2} til</span>
          )}
        </span>
      ),
    },
  ];

  const originColumns: Array<Column<Origin>> = [
    { key: "origin", header: "Domene", render: (o) => <b>{o.origin}</b> },
    {
      key: "requests",
      header: "Kall",
      numeric: true,
      render: (o) => o.requests.toLocaleString("nb-NO"),
    },
    {
      key: "last",
      header: "Sist",
      numeric: true,
      render: (o) => (
        <span className={styles.muted}>{formatDateTime(o.lastUsedAt)}</span>
      ),
    },
    {
      key: "ip",
      header: "Siste IP",
      render: (o) => <span className={styles.muted}>{o.lastIp || "–"}</span>,
    },
    {
      key: "ua",
      header: "Siste nettleser / klient",
      render: (o) => (
        <span className={styles.ua}>
          {o.lastUserAgent || "–"}
          {o.lastPath && <span className={styles.muted}>{o.lastPath}</span>}
        </span>
      ),
    },
  ];

  const stats: Array<[string, React.ReactNode]> = [
    ["Aktive nøkler", isLoading ? "–" : active.length],
    [
      "Moskeer med nøkkel",
      isLoading ? "–" : new Set(active.map((k) => k.orgNr)).size,
    ],
    ["Brukt siste 30 dager", isLoading ? "–" : usedLast30],
    ["Kall siste 30 dager", isLoading ? "–" : calls30.toLocaleString("nb-NO")],
  ];

  return (
    <>
      <PageHeader
        title="API-nøkler"
        subtitle="Alle API-nøkler moskeene har laget: hvem som laget dem, og når og hvor de er brukt."
        actions={
          <>
            <Button
              variant="secondary"
              icon={<Icon name="swap" size={16} />}
              onClick={load}
              loading={isLoading}
            >
              Oppdater
            </Button>
            <Button icon={<Icon name="plus" size={16} />} onClick={openCreate}>
              Ny nøkkel
            </Button>
          </>
        }
      />

      <div className={styles.stats}>
        {stats.map(([label, value]) => (
          <div key={label} className={styles.stat}>
            <b>{value}</b>
            <span>{label}</span>
          </div>
        ))}
      </div>

      <div className={styles.filters}>
        <TextField
          aria-label="Søk"
          type="search"
          placeholder="Søk etter moské, org.nr., navn, e-post eller domene"
          className={styles.search}
          fieldClassName={styles.searchField}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Toggle
          id="vis-tilbaketrukne"
          label="Vis tilbaketrukne"
          checked={showRevoked}
          onChange={(e) => setShowRevoked(e.target.checked)}
        />
      </div>

      <Card className={styles.tableCard}>
        {isLoading ? (
          <div className={styles.skeletons}>
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} height={40} />
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            rows={rows}
            rowKey={(k) => k.id}
            onRowClick={(k) => setDetails(k)}
            rowState={(k) => (k.revokedAt ? "dim" : undefined)}
            empty={<EmptyState icon="key" title="Ingen API-nøkler ennå" />}
          />
        )}
      </Card>

      <PageModal
        open={!!details}
        title={`${details?.mosqueName || details?.orgNr} – ${details?.name}`}
        icon="key"
        width={820}
        onClose={() => setDetails(null)}
        footer={
          <>
            {details && !details.revokedAt && (
              <Button
                variant="danger"
                icon={<Icon name="trash" size={16} />}
                onClick={() => setRevoking(details)}
              >
                Trekk tilbake nøkkelen
              </Button>
            )}
            <Button variant="secondary" onClick={() => setDetails(null)}>
              Lukk
            </Button>
          </>
        }
      >
        <span className={styles.muted}>
          <code className={styles.code}>{details?.keyPrefix}…</code> · laget{" "}
          {formatDateTime(details?.createdAt)} av{" "}
          {details?.createdByEmail || "ukjent"}
          {details?.revokedAt && (
            <> · trukket tilbake {formatDateTime(details.revokedAt)}</>
          )}
        </span>
        <h3 className={styles.h3}>Brukt fra (siste 30 dager)</h3>
        <Table
          columns={originColumns}
          rows={details?.origins ?? []}
          rowKey={(o) => o.origin}
          compact
          empty={
            <EmptyState icon="link" title="Ikke brukt de siste 30 dagene" />
          }
        />
      </PageModal>

      <PageModal
        open={createOpen}
        title="Ny API-nøkkel for en moské"
        icon="key"
        width={560}
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Avbryt
            </Button>
            <Button onClick={create} loading={creating} disabled={!createOrgNr}>
              Lag nøkkel
            </Button>
          </>
        }
      >
        {mosques === null ? (
          <Skeleton height={40} />
        ) : (
          <Select
            label="Moské"
            value={createOrgNr}
            onChange={(e) => setCreateOrgNr(e.target.value)}
            options={[
              { value: "", label: "Velg moské" },
              ...mosques.map((m) => ({
                value: m.orgNr,
                label: `${m.name || m.orgName} (${m.orgNr})`,
              })),
            ]}
            help="Nøkkelen hører til moskeen, og moskeens admins ser den på API-siden sin."
            autoFocus
          />
        )}
        <TextField
          label="Navn (valgfritt)"
          placeholder="F.eks. Nettsiden"
          value={createName}
          onChange={(e) => setCreateName(e.target.value)}
          maxLength={120}
        />
        {createError && <Alert tone="error">{createError}</Alert>}
      </PageModal>

      <PageModal
        open={!!newKey}
        title={`Ny API-nøkkel · ${newKey?.mosque ?? ""}`}
        icon="key"
        width={620}
        onClose={() => setNewKey(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setNewKey(null)}>
              Lukk
            </Button>
            <Button
              onClick={() => newKey && copy(newKey.key)}
              icon={
                <Icon
                  name={copied ? "check" : "copy"}
                  size={16}
                  strokeWidth={2}
                />
              }
              autoFocus
            >
              {copied ? "Kopiert" : "Kopier nøkkelen"}
            </Button>
          </>
        }
      >
        <Alert tone="warn">
          Kopier nøkkelen nå. Den vises bare denne ene gangen.
        </Alert>
        <pre className={styles.keyCode}>{newKey?.key}</pre>
      </PageModal>

      <ConfirmDialog
        open={!!revoking}
        title="Trekke tilbake nøkkelen?"
        tone="danger"
        confirmLabel="Trekk tilbake"
        onConfirm={revoke}
        onCancel={() => setRevoking(null)}
      >
        «{revoking?.name}» for {revoking?.mosqueName || revoking?.orgNr} slutter
        å virke med en gang.
      </ConfirmDialog>
    </>
  );
};

export default ApiKeysPage;
